import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Alert,
  CircularProgress
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Unauthorized = () => {
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  
  const userEmail = queryParams.get('email');
  const userName = queryParams.get('name');
  const role = queryParams.get('role') || '';
  const reason = queryParams.get('reason') || '';
  const netid = queryParams.get('netid') || '';

  const isMissingAffiliate = reason === 'missing_affiliate';
  // Check if it's likely a student trying to be an employer
  const isStudentAsEmployer = isMissingAffiliate && role === 'Employer' && (userEmail?.includes('@tamu.edu') && !userEmail?.includes('@email.tamu.edu') === false);

  const affiliateSignupUrl = 'https://ran.tamids.tamu.edu/sso';

  const handleRequestAccess = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/sso/request-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, name: userName }),
      });
      const data = await response.json();
      if (data.success) setRequestSent(true);
      else throw new Error(data.error || 'Failed to send request');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 600, textAlign: 'center' }}>
          <img src="/tamu-logo.png" alt="Texas A&M University" style={{ maxWidth: 300, marginBottom: '2rem' }} />
          
          <Typography variant="h4" gutterBottom color="error">
            Access Restricted
          </Typography>

          <Alert icon={<ErrorOutlineIcon />} severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
            {isMissingAffiliate 
              ? `We couldn't verify an active Faculty/Staff Affiliate profile for ${netid || 'your account'}.`
              : 'Your account does not have authorized access to this area.'}
          </Alert>
          
          <Box sx={{ mb: 4 }}>
            {isMissingAffiliate && role === 'Employer' ? (
              <Typography variant="body1" paragraph>
                <strong>Note for Students:</strong> If you are a student looking to browse or apply for projects, please return to the login page and select <strong>"Student Login"</strong>. Students cannot create Employer accounts.
                <br /><br />
                <strong>Note for Faculty/Staff:</strong> Employer access requires an active profile in the TAMIDS Research Affiliate Network (RAN). 
              </Typography>
            ) : (
              <Typography variant="body1" paragraph>
                To access DATA2REALITY as an Employer, you need to be a verified Faculty or Staff member.
              </Typography>
            )}
          </Box>

          {isMissingAffiliate && role === 'Employer' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button variant="contained" color="secondary" onClick={() => window.location.href = '/'}>
                Go to Student Login
              </Button>
              <Button variant="outlined" href={affiliateSignupUrl} target="_blank">
                Faculty/Staff: Join RAN
              </Button>
            </Box>
          ) : requestSent ? (
            <Alert icon={<CheckCircleIcon />} severity="success">
              Access request sent. We will review your affiliation.
            </Alert>
          ) : (
            <Button variant="contained" onClick={handleRequestAccess} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Request Access'}
            </Button>
          )}

          <Button variant="text" onClick={() => window.location.href = '/'} sx={{ mt: 3 }}>
            Return Home
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default Unauthorized;
